import React, { useMemo, useRef } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import { RootState } from '../services/rootReducer';
import { selectAdditionalSources, AdditionalSourcesGroup, MISSION_FIELD } from './utils/additionalSources';
import styles from './css/AdditionalSources.module.css';

/**
 * Non-collapsible sidebar block listing curated external links for the mission(s)
 * the current filters leave in scope. Renders nothing when there is nothing to
 * show — see `selectAdditionalSources` for the rules.
 *
 * Plain <a> elements on purpose: the global index.css force-styles every `.btn`
 * with !important, so a react-bootstrap Button/Card would fight it.
 */
function AdditionalSources() {
    const { currentFacetMap, facetSelected, facetMap } = useSelector(
        (state: RootState) => ({
            currentFacetMap: state.Facets.currentFacetMap,
            facetSelected: state.Facets.facetSelected,
            facetMap: state.Facets.facetMap,
        }),
        shallowEqual
    );

    // getAvailableFacets (Facets_Slice.ts) stores one and the same reply object as
    // both facetMap and currentFacetMap. On a shared link it runs twice, and when
    // its second reply lands after the currentFacets reply for the link's filters,
    // currentFacetMap is the unfiltered list again until the next currentFacets
    // reply. That state says nothing about the active filters, so keep what is
    // shown instead of hiding the block for a moment. A selected mission does not
    // depend on the facet maps, so that case is never held.
    const shownGroups = useRef<AdditionalSourcesGroup[]>([]);
    const groups = useMemo(() => {
        const missionSelected = (facetSelected ?? []).some(
            (entry) => entry.split('@')[1] === MISSION_FIELD && entry.split('@')[0] !== ''
        );
        if (!missionSelected && currentFacetMap === facetMap) {
            return shownGroups.current;
        }
        return selectAdditionalSources(facetSelected, currentFacetMap, facetMap);
    }, [facetSelected, currentFacetMap, facetMap]);
    shownGroups.current = groups;

    if (groups.length === 0) {
        return null;
    }

    return (
        <section className={styles.block} aria-labelledby="additionalSourcesHeading">
            <h2 id="additionalSourcesHeading" className={styles.heading}>
                Related Resources
            </h2>
            {groups.map((group) => (
                <React.Fragment key={group.mission}>
                    <h3 className={styles.missionTitle}>{group.mission}</h3>
                    <ul className={styles.linkList}>
                        {group.links.map((link) => (
                            <li key={`${link.url}|${link.label}`} className={styles.linkItem}>
                                <a
                                    className={styles.link}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </React.Fragment>
            ))}
        </section>
    );
}

export default React.memo(AdditionalSources);
